import { Sender } from '@ant-design/x'
import { Avatar, Button, Dropdown, Flex, Typography } from 'antd'
import { CSSProperties, useRef, useState } from 'react'

import type { GetRef, MenuProps } from 'antd'
import { OrganizationMemberFromContext } from '~/core/context/organization/hooks/useOrganizationMembers'
import { LoaderDots } from '~/features/chatV3/client/components/Thread/components/LoaderDots'
import { MixpanelClient } from '~/plugins/mixpanel/client'

import {
  SelectionType,
  suggestionKeyToGuideline,
  suggestionsBySelection,
} from './suggestions'

type Props = {
  agent: OrganizationMemberFromContext
  isUpdating?: boolean
  selectionType: SelectionType
  style?: CSSProperties
  onSubmit: (guidelines: string) => void
  onFocus?: () => void
  onBlur?: () => void
}

export const AgentChatInput: React.FC<Props> = ({
  agent,
  onSubmit,
  selectionType,
  isUpdating = false,
  style,
}) => {
  const mixpanel = MixpanelClient.use()

  const senderRef = useRef<GetRef<typeof Sender>>(null)
  const [value, setValue] = useState('')

  const handleSubmit = (input: string) => {
    const trimmed = input.trim()
    mixpanel.track('blogWriter.agent.assistant.button.clicked', {
      value: trimmed,
    })
    if (trimmed) {
      onSubmit(trimmed)
      setValue('')
    }
  }

  const submitSuggestion = (text: string) => {
    setValue(text)

    requestAnimationFrame(() => {
      handleSubmit(text)
    })
  }

  const onDropdownClick: MenuProps['onClick'] = ({ key }) => {
    const suggestionText = suggestionKeyToGuideline[key]
    if (suggestionText) {
      submitSuggestion(suggestionText)
    }
  }

  const suggestionItems = suggestionsBySelection[selectionType]

  return (
    <Flex
      vertical
      style={style}
      className="w-full"
      gap={4}
      onMouseDown={e => {
        e.stopPropagation()
      }}
    >
      <Flex align="center" gap={8}>
        <Avatar
          size={54}
          src={agent.appSession.pictureUrl}
          style={{ flexShrink: 0, border: '1px solid grey' }}
        />
        <Flex vertical style={{ flex: 1 }} gap={4}>
          {suggestionItems.length > 0 && (
            <Dropdown
              menu={{ items: suggestionItems, onClick: onDropdownClick }}
              placement="topLeft"
              trigger={['click']}
            >
              <Button
                size="small"
                style={{ width: 'fit-content', opacity: 1 }}
                loading={isUpdating}
              >
                ✨ Suggestions
              </Button>
            </Dropdown>
          )}

          <Sender
            ref={senderRef}
            value={value}
            onChange={setValue}
            onSubmit={handleSubmit}
            disabled={isUpdating}
            placeholder={
              !isUpdating ? `Ask ${agent.appSession.name} for updates...` : ''
            }
            prefix={
              isUpdating && (
                <Flex align="center" gap={8}>
                  <Typography.Text type="secondary">
                    {agent.appSession.name} is updating
                  </Typography.Text>
                  <LoaderDots />
                </Flex>
              )
            }
            loading={isUpdating}
            submitType="enter"
            style={{ backgroundColor: '#fff' }}
          />
        </Flex>
      </Flex>
    </Flex>
  )
}
